import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { orgsApi } from '../features/orgs/api.ts';
import type { OrgUserRow, OrgUserPatch } from '../features/users/api.ts';
import { dv, type Role } from '../i18n/dv.ts';
import { displayName, roleLabel } from '../lib/utils.ts';

const INVITE_ROLES: Exclude<Role, 'SYS_ADMIN'>[] = ['ADMIN', 'ENUMERATOR'];

const ROLES: Exclude<Role, 'SYS_ADMIN'>[] = ['ADMIN', 'ENUMERATOR'];

export function AdminOrgDetail(): JSX.Element {
  const { orgId } = useParams({ from: '/admin/orgs/$orgId' });
  const qc = useQueryClient();
  const { data: org } = useQuery({
    queryKey: ['admin', 'orgs', 'detail', orgId],
    queryFn: () => orgsApi.get(orgId),
  });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Exclude<Role, 'SYS_ADMIN'>>('ENUMERATOR');
  const [sent, setSent] = useState(false);

  const invite = useMutation({
    mutationFn: () => orgsApi.bootstrapOrgAdmin(orgId, email, role),
    onSuccess: () => {
      setSent(true);
      setEmail('');
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: 'ACTIVE' | 'SUSPENDED') => orgsApi.setStatus(orgId, status),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'orgs', 'detail', orgId] }),
  });

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['admin', 'orgs', 'users', orgId],
    queryFn: () => orgsApi.listUsers(orgId),
  });

  const invalidateUsers = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'orgs', 'users', orgId] });
    void qc.invalidateQueries({ queryKey: ['admin', 'orgs', 'detail', orgId] });
  };

  const updateUser = useMutation({
    mutationFn: (v: { id: string; patch: OrgUserPatch }) =>
      orgsApi.updateUser(orgId, v.id, v.patch),
    onSuccess: invalidateUsers,
  });

  // Per-row designation editing.
  const [desigEditId, setDesigEditId] = useState<string | null>(null);
  const [desigDv, setDesigDv] = useState('');
  const [desigEn, setDesigEn] = useState('');
  const startDesigEdit = (u: OrgUserRow) => {
    setDesigEditId(u.id);
    setDesigDv(u.designationDv ?? '');
    setDesigEn(u.designationEn ?? '');
  };
  const saveDesig = (id: string) => {
    updateUser.mutate(
      { id, patch: { designationDv: desigDv.trim() || null, designationEn: desigEn.trim() || null } },
      { onSuccess: () => setDesigEditId(null) },
    );
  };

  const [resetSentId, setResetSentId] = useState<string | null>(null);
  const resetUser = useMutation({
    mutationFn: (id: string) => orgsApi.resetUserPassword(orgId, id),
    onSuccess: (_d, id) => { setResetSentId(id); setTimeout(() => setResetSentId(null), 4000); },
  });

  return (
    <div className="page space-y-6">
      <Link to="/admin/orgs" className="text-sm text-primary hover:underline">
        {dv.common.back}
      </Link>

      {/* SYS_ADMIN break-glass screen. Primary user management lives in Wapplify Office. */}
      <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        {dv.users.breakGlassNote}
      </p>

      {org ? (
        <>
          <header className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-serif text-3xl tracking-tight">{org.name}</h1>
              <p className="text-sm text-muted-foreground mt-1" dir="ltr">
                {org.contactEmail}
              </p>
              <p className="text-sm text-muted-foreground">
                <span
                  className={`status-label rounded-full px-2 py-0.5 text-xs ${
                    org.status === 'ACTIVE'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {org.status === 'ACTIVE' ? dv.common.active : dv.common.suspended}
                </span>
                {' '}· {org._count.users} {dv.orgs.users}
              </p>
            </div>
            <button
              onClick={() =>
                setStatus.mutate(org.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE')
              }
              disabled={setStatus.isPending}
              className="btn-secondary btn-sm"
            >
              {org.status === 'ACTIVE' ? dv.orgs.suspend : dv.orgs.activate}
            </button>
          </header>

          <section className="card-paper p-4 space-y-3">
            <h2 className="status-label text-muted-foreground">{dv.orgs.inviteAdmin}</h2>
            <form
              className="flex flex-col gap-2 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                if (email.trim()) invite.mutate();
              }}
            >
              <input
                aria-label={dv.auth.email}
                type="email"
                dir="ltr"
                placeholder={dv.auth.email}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setSent(false); }}
                required
                className="input flex-1"
              />
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as Exclude<Role, 'SYS_ADMIN'>)}
                aria-label={dv.orgs.adminRole}
                className="select sm:w-auto"
              >
                {INVITE_ROLES.map((r) => (
                  <option key={r} value={r}>{dv.roles[r]}</option>
                ))}
              </select>
              <button
                type="submit"
                disabled={invite.isPending || !email.trim()}
                className="btn-primary"
              >
                {dv.users.invite}
              </button>
            </form>
            {sent && (
              <p className="text-sm text-emerald-700">{dv.orgs.inviteSent}</p>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="status-label text-muted-foreground">{dv.users.title}</h2>
            <div className="card-paper divide-y divide-border">
              {(users ?? []).map((u) => (
                <div
                  key={u.id}
                  className={`flex flex-wrap items-center justify-between gap-3 p-4 ${!u.isActive ? 'opacity-60' : ''}`}
                >
                  <div>
                    <p className="font-medium">
                      {displayName(u)}
                      {u.isOrgAdmin && (
                        <span className="ms-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                          {dv.orgAdminBadge}
                        </span>
                      )}
                      {!u.isActive && (
                        <span className="ms-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                          {dv.common.inactive}
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {u.designationEn ?? roleLabel(u.role, u.zoneRole, dv.roles)}
                    </p>
                    <p className="text-sm text-muted-foreground" dir="ltr">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={u.role ?? ''}
                      disabled={updateUser.isPending || u.role === null}
                      title={u.role === null ? 'Zone-only user: no census role' : undefined}
                      onChange={(e) => updateUser.mutate({ id: u.id, patch: { role: e.target.value as Exclude<Role, 'SYS_ADMIN'> } })}
                      className="select w-auto px-2 py-1"
                    >
                      {u.role === null && <option value="">—</option>}
                      {ROLES.map((r) => <option key={r} value={r}>{dv.roles[r]}</option>)}
                    </select>
                    <button
                      onClick={() => (desigEditId === u.id ? setDesigEditId(null) : startDesigEdit(u))}
                      className="btn-secondary btn-sm"
                    >
                      {dv.users.editDesignation}
                    </button>
                    <button
                      onClick={() => updateUser.mutate({ id: u.id, patch: { isOrgAdmin: !u.isOrgAdmin } })}
                      className="btn-secondary btn-sm"
                    >
                      {dv.users.orgAdmin}: {u.isOrgAdmin ? dv.common.active : dv.common.none}
                    </button>
                    <button
                      onClick={() => updateUser.mutate({ id: u.id, patch: { isActive: !u.isActive } })}
                      className="btn-secondary btn-sm"
                    >
                      {u.isActive ? dv.users.deactivate : dv.users.activate}
                    </button>
                    <button
                      onClick={() => { if (window.confirm(dv.users.sendReset)) resetUser.mutate(u.id); }}
                      className="btn-secondary btn-sm"
                    >
                      {dv.users.sendReset}
                    </button>
                    {resetSentId === u.id && (
                      <span className="text-sm text-emerald-700">{dv.users.resetSent}</span>
                    )}
                  </div>
                  {desigEditId === u.id && (
                    <div className="flex w-full flex-wrap items-end gap-3 border-t border-border pt-3">
                      <div className="space-y-1">
                        <label className="field-label">{dv.users.designationDv}</label>
                        <input
                          value={desigDv}
                          maxLength={120}
                          onChange={(e) => setDesigDv(e.target.value)}
                          className="input"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="field-label">{dv.users.designationEn}</label>
                        <input
                          dir="ltr"
                          value={desigEn}
                          maxLength={120}
                          onChange={(e) => setDesigEn(e.target.value)}
                          className="input"
                        />
                      </div>
                      <button
                        onClick={() => saveDesig(u.id)}
                        disabled={updateUser.isPending}
                        className="btn-primary"
                      >
                        {dv.common.save}
                      </button>
                      <button
                        onClick={() => setDesigEditId(null)}
                        className="btn-secondary"
                      >
                        {dv.common.cancel}
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {(users ?? []).length === 0 && (
                <p className="p-4 text-center text-sm text-muted-foreground">
                  {usersLoading ? dv.common.loading : dv.users.empty}
                </p>
              )}
            </div>
          </section>
        </>
      ) : (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {dv.common.loading}
        </p>
      )}
    </div>
  );
}
